import { html, type Html, Page, View } from 'rune-ts';
import klass from './CreatorShopFundings.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { getSharedData } from '../../../shared/util/rune';
import { footer_links } from '../../components/cells/Footer/constant';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { MembershipTypeS } from '../../../features/Membership/outbound/share';
import { Pagination, PaginationChangeEvent } from '../../components/cells/Pagination/Pagination';
import { type LocalsStoreMembership } from '@services/MarppleShop/shared/type/locals.ts';
import { SubHeaderMobile } from '@services/MarppleShop/renderApp/components/cells/Header/Global/views/SubHeaderMobile.ts';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import { OnboardingApiS } from '@services/MarppleShop/marppleShopStudioApp/features/Onboarding/outbound/share.ts';
import { EventLogConstantS } from '@services/MarppleShop/features/EventLog/outbound/share.ts';
import { identity } from '@fxts/core';
import { FundingListFetchOption, FundingListResponse } from '@services/MarppleShop/features/Funding';
import { FundingCardList } from '@services/MarppleShop/renderApp/pages/CreatorShopFundings/FundingCardList.ts';
import { loading, loadingEnd } from '@services/MarppleShop/shared/client/global';
import { updateUrlParams } from '@services/MarppleShop/shared/util';
import { getFundingList } from '@services/MarppleShop/features/Funding/api';
import { GlobalTimerManager } from '@services/MarppleShop/shared/util/timer';
import { CreatorShopMainBanner24View } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShopMainBanner24View.ts';
import { CreatorMainProfile } from '@services/MarppleShop/features/Creator/type';
import { LnbCreatorShop24View } from '@services/MarppleShop/renderApp/components/cells/Header/LnbCreatorShop24View.ts';
import { TCreatorShopLnbMenu } from '@services/MarppleShop/shared/constant';
import { CreatorConstantS } from '@services/MarppleShop/features/Creator/outbound/share.ts';
import { BestCreatorRanking } from '@services/MarppleShop/features/CreatorRank/type/creator-rank.type.ts';
import { CreatorShopSubHeader } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorShopSubHeader.ts';
import { CreatorShopMainBanner24MoView } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShopMainBanner24MoView.ts';

export type CreatorShopFundingsPageData = {
  store_id: number;
  layout_options: BaseLayoutOptions;
  is_store_owner: boolean;
  membership?: LocalsStoreMembership & {
    my: {
      token?: string;
      memberships?: MembershipTypeS.MembershipSummary[];
    };
  };
  server_timestamp: number;
  adult_check_needed: boolean;
  is_login: boolean;
  fundings: FundingListResponse['data'];
  fetch_options: FundingListFetchOption;
  total_count: number;
  creator: CreatorMainProfile;
  menus: TCreatorShopLnbMenu[];
  best_store_ranking: BestCreatorRanking | undefined;
};

class CreatorShopFundingsBody extends View<CreatorShopFundingsPageData> {
  private fundingCardListView = new FundingCardList({
    fundings: this.data.fundings,
    server_timestamp: this.data.server_timestamp,
    is_mobile: this.data.layout_options.is_mobile,
    userId: this.data.layout_options.user?.id,
    adult_check_needed: this.data.adult_check_needed,
  });

  private paginationView = new Pagination({
    page: this.data.fetch_options.page,
    size: this.data.layout_options.is_mobile ? 5 : 10,
    total: Pagination.calcTotal({
      total_count: this.data.total_count,
      limit_per_page: 60,
    }),
  });

  protected override template({
    creator,
    is_login,
    adult_check_needed,
    layout_options,
    membership,
    menus,
    best_store_ranking,
    store_id,
  }: CreatorShopFundingsPageData) {
    const sharedData = getSharedData(this);

    const MainBannerView = sharedData.is_mobile ? CreatorShopMainBanner24MoView : CreatorShopMainBanner24View;

    const LnbView = sharedData.is_mobile ? CreatorShopSubHeader : LnbCreatorShop24View;

    return html`
      <div class="${klass.page}">
        ${new MainBannerView({
          cover:
            (creator.is_adult && adult_check_needed) ||
            (sharedData.is_mobile
              ? creator.banner_img_mo === CreatorConstantS.banner_img
              : creator.banner_img === CreatorConstantS.banner_img)
              ? 'No Cover'
              : 'Default',
          show_adult_creator_thumbnail: Boolean(creator.is_adult && adult_check_needed),
          is_owner: creator.user_id === layout_options.user?.id,
          creator,
          is_login,
          membership,
          best_store_ranking,
          store: {
            id: store_id,
          },
        })}
        <div class="${klass.sub_header}">
          ${new LnbView({
            menus,
            plus_domain: sharedData?.plus_crew?.domain_name,
            focus_on_select: true,
          })}
        </div>

        <div class="${klass.body}">
          <div class="${klass.main}">
            ${this.fundingCardListView}
            ${this.paginationView.isHidden
              ? ''
              : html`<div class="${klass.pagination}">${this.paginationView}</div>`}
          </div>
        </div>
      </div>
    `;
  }

  protected override onRender() {
    SubHeaderMobile.handleChangeMenuOutside();
    this.delegate(PaginationChangeEvent, Pagination, async (event) => {
      try {
        const { page } = event.detail;

        this.paginationView.disableClick();
        loading();

        history.replaceState(null, '', updateUrlParams(location.href, { page }));

        const { data } = await getFundingList(this.data.store_id, {
          ...this.data.fetch_options,
          page,
          userId: this.data.layout_options.user?.id,
        });

        this.fundingCardListView.update(data);
        loadingEnd();
        this.paginationView.enableClick();
        window?.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      } catch (e) {
        loadingEnd();
        this.paginationView.enableClick();
      }
    });
  }
}

export class CreatorShopFundingsPage extends Page<CreatorShopFundingsPageData> {
  public static LIMIT = 50;
  protected override template(): Html {
    const sharedData = getSharedData(this);
    const { layout_options } = this.data;

    return html` <div>
      ${new LayoutView(
        {
          body: new CreatorShopFundingsBody(this.data),
          footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
        },
        {
          ...layout_options,
          full_width: true,
          has_bottom_tab_bar: !sharedData?.webviewapp,
          selected_bottom_tab_idx: -1,
        },
      )}
    </div>`;
  }

  override onRender() {
    /**
     * @desc 마플샵 크리에이터 온보딩 리뷰 관련 완료
     * @see EventLogConstantS.ACTION_TYPE.CREATOR_STUDIO_ONBOARDING.FIRST_CHAT_CHEER
     */
    if (UtilS.isMarppleShopApp() && box.sel('store_user_id') === box.sel('is_user->id') && ET.lang === 'kr') {
      OnboardingApiS.logCreatorStudioOnboardingAction(
        { store_id: box.sel('store_id') },
        {
          action_type: EventLogConstantS.ACTION_TYPE.CREATOR_STUDIO_ONBOARDING.FIRST_CHAT_CHEER,
          user_id: box.sel('store_user_id'),
        },
      ).catch(identity);
    }

    const globalTimer = GlobalTimerManager.getInstance();
    globalTimer.start(1000);
  }
}
